import { IS_AU_INSTANCE } from "utils/environmentVariables";
import {
  AccountRepresentatives,
  RepresentativeInformation,
  UserInfo,
  UTMParams,
} from "./types";

export const getQueryParamsFromObject = (
  obj: Record<string, string | boolean | number>,
  filterUndefinedOrNull: boolean = true
): string => {
  let objectEntries = Object.entries(obj);

  if (filterUndefinedOrNull) {
    objectEntries = objectEntries.filter(([_, value]) => {
      return value !== undefined && value !== null;
    });
  }

  if (objectEntries.length === 0) {
    return "";
  }

  const queryParams = objectEntries
    .map(
      ([key, value]) =>
        `${encodeURIComponent(key)}=${encodeURIComponent(value)}`
    )
    .filter((param) => param) // Filter out any empty strings from the map step
    .join("&");

  return queryParams;
};

export const getScheduleDevelopDemoUrl = (
  companyAccountId?: number,
  rep?: AccountRepresentatives | null,
  isFreeTrial?: boolean,
  utmParams?: UTMParams,
  overrideScheduleOnceName?: boolean
) => {
  const { content, campaign, source, medium } = utmParams ?? {
    content: "Manager_guide_Adopt_Develop_V1",
    campaign: "Develop",
    source: "Develop",
    medium: companyAccountId ?? "",
  };
  const CALENDLY_BASE_URL = "https://calendly.com";
  const utmParametersLink = `?utm_content=${content}&utm_campaign=${campaign}&utm_source=${source}&utm_medium=${medium}`;
  let calendlyLink: string | null = null;

  // If not free trial and Customer Success Manager is available, use Customer Success Manager
  if (!isFreeTrial) {
    calendlyLink = getCalendlyLinkFromRep(
      rep?.customerSuccessManager,
      utmParametersLink,
      overrideScheduleOnceName
    );
  }

  // If free trial and Account Manager is available, use Account Manager
  if (isFreeTrial) {
    calendlyLink = getCalendlyLinkFromRep(
      rep?.accountManager,
      utmParametersLink,
      overrideScheduleOnceName
    );
  }

  // If a calendlyLink was defined use it.
  if (calendlyLink) {
    return calendlyLink;
  }

  // Otherwise fallback to default
  return `${CALENDLY_BASE_URL}/d/${
    IS_AU_INSTANCE ? "ckkp-rmk-9d7" : "cpz5-vqp-qq6"
  }/subscription-add-on`;
};

const getCalendlyLinkFromRep = (
  rep?: RepresentativeInformation | null,
  utmParametersLink?: string,
  overrideScheduleOnceName?: boolean
) => {
  if (rep?.scheduleOnceName && !overrideScheduleOnceName) {
    return rep.scheduleOnceName;
  }
  if (rep?.firstName && rep?.lastName) {
    const csmFirstName = rep.firstName.replace(/'/g, "").toLowerCase();
    const csmLastName = rep.lastName.replace(/'/g, "").toLowerCase();
    return `https://calendly.com/criteria-${csmFirstName}-${csmLastName}${utmParametersLink}`;
  }
  return null;
};

export const getEmailsOfUsersAlreadyInCompany = (
  usersInfoById: { [userAccountId: string | number]: UserInfo },
  emailAddresses: string[]
) => {
  const emailsOfUsersAlreadyInCompany: string[] = [];
  const emailAddressesCopy = [...emailAddresses].map((emailAddress) =>
    emailAddress.toLowerCase()
  );
  const errorMap: Record<string, string> = {};
  Object.values(usersInfoById).forEach((user) => {
    const emailAddressIndex = emailAddressesCopy.indexOf(
      user.emailAddress.toLowerCase()
    );

    if (emailAddressIndex !== -1) {
      emailsOfUsersAlreadyInCompany.push(user.emailAddress);
      emailAddressesCopy.splice(emailAddressIndex, 1);
      errorMap[
        user.emailAddress
      ] = `'${user.emailAddress}' is already in company.`;
    }
  });

  return { emailsOfUsersAlreadyInCompany, errorMap };
};
